/**
 * Classes externes
 */
// NOTE: Utilisez les classes/interfaces révisées des modèles précédents pour CustomerInfo
import { CustomerInfo, ParticularCustomerInformation, ProfessionalCustomerInformation as ProfessionalCustomerInformation } from "./CustomerInfo"; 
import { MetersInformations } from "./MetersInformations";
import { Row } from "@/components/commons/Generics/GenericTable/GenericTableRow";
import { DisplayedInfo } from "./DisplayedInfo";

/**
 * Enumérations
 */
import SMACertificateEnum from "@/enums/Administration/SMA/SMACertificateEnum";
import CertificateStateEnum from "@/enums/Administration/SMA/CertificateStateEnum";
import ProfessionalTypeEnum from "@/enums/Administration/SMA/ProfessionalTypeEnum";

/**
 * Traduction
 */
import { i18n } from '@/main';

// ---

/**
 * Mandat distributeur (Complet)
 */
class SMACertificate extends Row {
    private certificateId: string;
    private distributor: SMACertificateEnum;
    private certificateName: string;
    private contractRef: string | null;
    private endDate: Date;
    private customerInfo: CustomerInfo;
    private meterInformations: MetersInformations;
    private status: CertificateStateEnum;
    private customerValidation: string;
    private dateCustomerValidation: string;
    private supervisorValidation: string;
    private dateSupervisorValidation: string;
    private resources: Array<Resource>;

    /**
     * Constructeur
     * @param data Les données pour initialiser le certificat.
     */
    constructor(data: any) {
        super(data.CertificateId ?? '0', data.CertificateName ?? '', {});
        // Initialisation des propriétés privées avec valeurs par défaut ou valeurs fournies
        this.certificateId = data.CertificateId ?? '0';
        this.certificateName = data.CertificateName ?? '';
        this.distributor = data.Distributor ?? SMACertificateEnum.ENEDIS;
        this.contractRef = data.ContractRef;
        this.endDate = data.EndDate ? new Date(data.EndDate) : new Date(new Date().setFullYear(new Date().getFullYear() + 1));
        this.status = data.Status ?? CertificateStateEnum.None;
        this.meterInformations = new MetersInformations(data.MeterInformations?.Meters);
        this.customerInfo = data.customerInfo ?? new ProfessionalCustomerInformation({});
        this.customerValidation = data.CustomerValidation ?? '';
        this.dateCustomerValidation = data.DateCustomerValidation ?? '';
        this.supervisorValidation = data.SupervisorValidation ?? '';
        this.dateSupervisorValidation = data.DateSupervisorValidation ?? '';
        this.resources = data.Resources?.map((r: any) => new Resource(r)) ?? [];
    }

    // --- ACCESSEURS (GETTERS) ---
    
    public get CertificateId(): string { return this.certificateId; }
    public get Distributor(): SMACertificateEnum { return this.distributor; }
    public get CertificateName(): string { return this.certificateName; }
    public get ContractRef(): string { return this.contractRef; }
    public get EndDate(): Date { return this.endDate; }
    public get CustomerInfo(): CustomerInfo { return this.customerInfo; }
    public get MeterInformations(): MetersInformations { return this.meterInformations; }
    public get Status(): CertificateStateEnum { return this.status; }
    public get CustomerValidation(): string { return this.customerValidation; }
    public get DateCustomerValidation(): string { return this.dateCustomerValidation; }
    public get SupervisorValidation(): string { return this.supervisorValidation; }
    public get DateSupervisorValidation(): string { return this.dateSupervisorValidation; }
    public get Resources(): Array<Resource> { return this.resources; }

    // --- MUTATEURS (SETTERS) ---
    public set CertificateId(value: string) { this.certificateId = value; }
    public set Distributor(value: SMACertificateEnum) { this.distributor = value; }
    public set CertificateName(value: string) { this.certificateName = value; }
    public set ContractRef(value: string) { this.contractRef = value; }
    public set EndDate(value: Date) {this.endDate = value; }
    public set CustomerInfo(value: CustomerInfo) { this.customerInfo = value; }
    public set MeterInformations(value: MetersInformations) { this.meterInformations = value; }
    public set Status(value: CertificateStateEnum) { this.status = value; }
    public set CustomerValidation(value: string) { this.customerValidation = value; }
    public set DateCustomerValidation(value: string) { this.dateCustomerValidation = value; }
    public set SupervisorValidation(value: string) { this.supervisorValidation = value; }
    public set DateSupervisorValidation(value: string) { this.dateSupervisorValidation = value; }
    public set Resources(value: Array<Resource>) { this.resources = value; }

    // Récupération des données client type professionnel du mandat
    public GetProfessionalInformations(): DisplayedInfo[] {
        return [
            { labelKey: 'CompanyName', value: (this.CustomerInfo as ProfessionalCustomerInformation).CompanyName ?? " " },
            { labelKey: 'ProfessionalType', value: i18n.t(ProfessionalTypeEnum[(this.CustomerInfo as ProfessionalCustomerInformation).ProfessionalType]).toString() ?? " "},
            { labelKey: 'Address', value: (this.CustomerInfo as ProfessionalCustomerInformation).Address ?? " " },
            { labelKey: 'Zipcode', value: (this.CustomerInfo as ProfessionalCustomerInformation).Zipcode ?? " " },
            { labelKey: 'City', value: (this.CustomerInfo as ProfessionalCustomerInformation).City ?? " " },
            { labelKey: 'Function', value: (this.CustomerInfo as ProfessionalCustomerInformation).Function ?? " " }
        ];
    }

    // Récupération des données client type particulier du mandat
    public GetParticularInformations(): DisplayedInfo[] {
        return [
            { labelKey: 'LastName', value: (this.CustomerInfo as ParticularCustomerInformation).LastName ?? " " },
            { labelKey: 'FirstName', value: (this.CustomerInfo as ParticularCustomerInformation).FirstName ?? " "},
            { labelKey: 'Phone', value: (this.CustomerInfo as ParticularCustomerInformation).Phone ?? " "},
            { labelKey: 'Email', value: (this.CustomerInfo as ParticularCustomerInformation).Email ?? " "},
            { labelKey: 'CustomerAddress', value: (this.CustomerInfo as ParticularCustomerInformation).CustomerAddress ?? " "},
            { labelKey: 'CustomerZipcode', value: (this.CustomerInfo as ParticularCustomerInformation).CustomerZipcode ?? " "},
            { labelKey: 'CustomerCity', value: (this.CustomerInfo as ParticularCustomerInformation).CustomerCity ?? " "},
            { labelKey: 'Birthday', value: (this.CustomerInfo as ParticularCustomerInformation).Birthday ?? " "}
        ];
    }

    // Récupération des données générales du mandat
    public GetGeneralInformations(): DisplayedInfo[] {
        return [
            { labelKey: 'Distributor', value: SMACertificateEnum[this.Distributor] },
            { labelKey: 'EndDate', value: this.formatter.format(new Date(this.EndDate)) }
        ]
    }

    // Récupération des données compteurs du mandat
    public GetMetersInformations(): DisplayedInfo[] {
        return [
            { labelKey: "MetersCount", value: this.MeterInformations.Meters.length.toString() },
            { labelKey: "ConformMetersCount", value: this.MeterInformations.Meters.filter(m => m.Errors.length == 0).length.toString() },
            { labelKey: "InErrorMetersCount", value: this.MeterInformations.Meters.filter(m => m.Errors.length > 0).length.toString() }
        ];
    }

    // Récupération de l'historique du mandat
    public GetHistoricInformations(): DisplayedInfo[] {
        return [
            { labelKey: "DateCustomerValidation", value: this.dateCustomerValidation != "" ? this.formatter.format(new Date(this.dateCustomerValidation)) : "" },
            { labelKey: "DateSupervisorValidation", value: this.dateSupervisorValidation != "" ? this.formatter.format(new Date(this.dateSupervisorValidation)) : "" },
        ];
    }
    
    /**
     * Formatter de date
     */
    private formatter = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

class Resource
{
    private id: string;
    private name: string;
    private data: string;
    private extension: string;
    private type: string;

    constructor(data: any) {
        this.id = data.Id ?? '';
        this.name = data.Name ?? '';
        this.extension = data.Extension ?? '';
        this.type = data.Type ?? '';
        this.data = data.Data ?? '';
    }

    public get Id(): string { return this.id; }
    public get Name(): string { return this.name; }
    public get Extension(): string { return this.extension; }
    public get Type(): string { return this.type; }
    public get Data(): string { return this.data; }

    public set Id(value: string) { this.id = value; }
    public set Name(value: string) { this.name = value; }
    public set Extension(value: string) { this.extension = value; }
    public set Type(value: string) { this.type = value; }
    public set Data(value: string) { this.data = value; }
}

export {
    SMACertificate,
    Resource
}